'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  BREAKPOINT_TOLERANCE,
  FULLSCREEN_EVENTS,
} from './ui.video-player.consts';
import {
  findTriggeredBreakpoint,
  calculateTimelineClickPosition,
  timelinePercentageToTime,
  clamp,
  callHostMethod,
  isFullscreenActive,
  resolvePlayStartTime,
} from './ui.video-player.helpers';
import {
  Breakpoint,
  TrimRange,
  VideoPlayerState,
  VideoPlayerCallbacks,
  ProgressData,
} from './ui.video-player.types';

const SCRUB_MOVEMENT_THRESHOLD_PX = 3;

/**
 * Custom hook for video player state management
 */
export const useVideoPlayerState = (
  initialState: Partial<VideoPlayerState> = {}
) => {
  const [state, setState] = useState<VideoPlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 0.8,
    isMuted: false,
    isFullscreen: false,
    isLoading: true,
    hasError: false,
    errorMessage: '',
    reachedBreakpoints: new Set(),
    ...initialState,
  });

  const updateState = useCallback((updates: Partial<VideoPlayerState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  return { state, updateState };
};

/**
 * Custom hook for breakpoint management
 */
export const useBreakpointManager = (
  breakpoints: Breakpoint[],
  onBreakpointReach?: (breakpoint: Breakpoint) => void
) => {
  const [reachedBreakpoints, setReachedBreakpoints] = useState<Set<string>>(
    new Set()
  );

  const checkBreakpointReach = (currentTime: number) => {
    const breakpoint = findTriggeredBreakpoint(
      currentTime,
      breakpoints,
      reachedBreakpoints,
      BREAKPOINT_TOLERANCE
    );

    if (breakpoint) {
      setReachedBreakpoints((prev) => new Set([...prev, breakpoint.id]));
      onBreakpointReach?.(breakpoint);
    }
  };

  const resetBreakpoints = () => {
    setReachedBreakpoints(new Set());
  };

  return { reachedBreakpoints, checkBreakpointReach, resetBreakpoints };
};

/**
 * Custom hook for timeline interactions
 */
export const useTimelineControls = (
  duration: number,
  onSeek: (time: number) => void
) => {
  const timelineRef = useRef<HTMLDivElement>(null);

  const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!timelineRef.current || duration === 0) return;

    const percentage = calculateTimelineClickPosition(
      event,
      timelineRef.current
    );
    const newTime = timelinePercentageToTime(percentage, duration);

    if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
      onSeek(newTime);
    }
  };

  return { timelineRef, handleTimelineClick };
};

/**
 * Custom hook for fullscreen management
 */
export const useFullscreenControls = (
  containerRef: React.RefObject<HTMLDivElement | null>
) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      const element = containerRef.current;
      if (callHostMethod(element, 'requestFullscreen')) return;
      if (callHostMethod(element, 'webkitRequestFullscreen')) return;
      if (callHostMethod(element, 'mozRequestFullScreen')) return;
      callHostMethod(element, 'msRequestFullscreen');
      return;
    }

    if (callHostMethod(document, 'exitFullscreen')) return;
    if (callHostMethod(document, 'webkitExitFullscreen')) return;
    if (callHostMethod(document, 'mozCancelFullScreen')) return;
    callHostMethod(document, 'msExitFullscreen');
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(isFullscreenActive());
    };

    FULLSCREEN_EVENTS.forEach((event) => {
      document.addEventListener(event, handleFullscreenChange);
    });

    return () => {
      FULLSCREEN_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleFullscreenChange);
      });
    };
  }, []);

  return { isFullscreen, toggleFullscreen };
};

const handleStart = () => {
  // Video started loading
};

/**
 * Custom hook for video player event handlers
 */
export const useVideoPlayerEvents = (
  callbacks: VideoPlayerCallbacks,
  checkBreakpointReach: (currentTime: number) => void
) => {
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastUpdateTime = useRef<number>(0);
  const THROTTLE_INTERVAL = 16; // ~60fps

  const handlePlay = useCallback(() => {
    callbacks.onPlay();
  }, [callbacks]);

  const handlePause = useCallback(() => {
    callbacks.onPause();
  }, [callbacks]);

  const handleProgress = (progress: ProgressData) => {
    const now = performance.now();

    // Throttle updates to ~60fps for smooth progress bar
    if (now - lastUpdateTime.current >= THROTTLE_INTERVAL) {
      const playedSeconds = progress.playedSeconds;

      if (isFinite(playedSeconds) && playedSeconds >= 0) {
        callbacks.onTimeUpdate(playedSeconds);
        checkBreakpointReach(playedSeconds);
        lastUpdateTime.current = now;
      }
    }
  };

  const handleDuration = (duration: number) => {
    if (isFinite(duration) && duration >= 0) {
      callbacks.onDurationChange(duration);
    }
  };

  const handleError = (error: Error) => {
    callbacks.onError(error);
  };

  const handleReady = () => {
    callbacks.onReady();
  };

  // Cleanup animation frame on unmount
  useEffect(() => {
    const currentAnimationFrame = animationFrameRef.current;
    return () => {
      if (currentAnimationFrame) {
        cancelAnimationFrame(currentAnimationFrame);
      }
    };
  }, []);

  return {
    handlePlay,
    handlePause,
    handleProgress,
    handleDuration,
    handleError,
    handleReady,
    handleStart,
  };
};

interface UsePointerScrubParams {
  timelineRef: React.RefObject<HTMLDivElement | null>;
  duration: number;
  trimRange?: TrimRange | null;
  disabled?: boolean;
  onSeekCommit: (time: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

export interface UsePointerScrubResult {
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void;
  isScrubbing: boolean;
  scrubTime: number;
  wasDragRef: React.RefObject<boolean>;
}

export const usePointerScrub = ({
  timelineRef,
  duration,
  trimRange,
  disabled,
  onSeekCommit,
  onScrubStart,
  onScrubEnd,
}: UsePointerScrubParams): UsePointerScrubResult => {
  const isScrubbingRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const pointerDownXRef = useRef(0);
  const wasDragRef = useRef(false);

  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubTime, setScrubTime] = useState(0);

  useEffect(() => {
    if (!isScrubbing) return;

    const safetyNetUp = () => {
      if (!isScrubbingRef.current) return;
      isScrubbingRef.current = false;
      wasDragRef.current = true;
      const el = timelineRef.current;
      const pid = pointerIdRef.current;
      if (el && pid !== null) {
        try {
          if (el.hasPointerCapture(pid)) {
            el.releasePointerCapture(pid);
          }
        } catch {
          /* */
        }
      }
      pointerIdRef.current = null;
      setIsScrubbing(false);
      onScrubEnd?.();
    };

    window.addEventListener('pointerup', safetyNetUp);
    window.addEventListener('pointercancel', safetyNetUp);
    return () => {
      window.removeEventListener('pointerup', safetyNetUp);
      window.removeEventListener('pointercancel', safetyNetUp);
    };
  }, [isScrubbing, onScrubEnd, timelineRef]);

  const computeTimeFromEvent = (
    event: React.PointerEvent<HTMLDivElement>
  ): number | null => {
    const el = timelineRef.current;
    if (!el || duration <= 0) return null;
    const percentage = clamp(calculateTimelineClickPosition(event, el), 0, 1);
    const time = timelinePercentageToTime(percentage, duration);
    const clamped = trimRange
      ? clamp(time, trimRange.startTime, trimRange.endTime)
      : clamp(time, 0, duration);
    return Number.isFinite(clamped) ? clamped : null;
  };

  const resetState = () => {
    const el = timelineRef.current;
    const pid = pointerIdRef.current;
    if (el && pid !== null) {
      try {
        if (el.hasPointerCapture(pid)) {
          el.releasePointerCapture(pid);
        }
      } catch {
        // pointer already released, ignore
      }
    }
    isScrubbingRef.current = false;
    pointerIdRef.current = null;
    setIsScrubbing(false);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    wasDragRef.current = false;
    if (disabled) return;
    pointerIdRef.current = event.pointerId;
    pointerDownXRef.current = event.clientX;
    isScrubbingRef.current = false;
  };

  const armScrub = () => {
    const el = timelineRef.current;
    const pid = pointerIdRef.current;
    if (!el || pid === null) return false;
    try {
      el.setPointerCapture(pid);
    } catch {
      pointerIdRef.current = null;
      return false;
    }
    isScrubbingRef.current = true;
    setIsScrubbing(true);
    onScrubStart?.();
    return true;
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (
      pointerIdRef.current === null ||
      pointerIdRef.current !== event.pointerId
    ) {
      return;
    }

    if (!isScrubbingRef.current) {
      const dx = Math.abs(event.clientX - pointerDownXRef.current);
      if (dx < SCRUB_MOVEMENT_THRESHOLD_PX) return;
      if (!armScrub()) return;
    }

    const time = computeTimeFromEvent(event);
    if (time === null) return;

    setScrubTime(time);
  };

  const finishScrub = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isScrubbingRef.current) return;
    isScrubbingRef.current = false;
    wasDragRef.current = true;
    onScrubEnd?.();
    const time = computeTimeFromEvent(event);
    if (time !== null) {
      setScrubTime(time);
      onSeekCommit(time);
    }
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      pointerIdRef.current === null ||
      pointerIdRef.current !== event.pointerId
    ) {
      return;
    }
    finishScrub(event);
    resetState();
  };

  const onPointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      pointerIdRef.current === null ||
      pointerIdRef.current !== event.pointerId
    ) {
      return;
    }
    if (isScrubbingRef.current) {
      isScrubbingRef.current = false;
      onScrubEnd?.();
    }
    resetState();
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    isScrubbing,
    scrubTime,
    wasDragRef,
  };
};

interface UseTrimPlayheadParams {
  trimRange?: TrimRange | null;
  isTrimMode: boolean;
  duration?: number;
  isPlaying: boolean;
  currentTime: number;
  seekTo: (time: number) => void;
  seekToUnclamped: (time: number) => void;
  play: () => void;
  pause: () => void;
  onTrimRangeChange?: (range: TrimRange) => void;
}

export const useTrimPlayhead = ({
  trimRange,
  isTrimMode,
  duration,
  isPlaying,
  currentTime,
  seekTo,
  seekToUnclamped,
  play,
  pause,
  onTrimRangeChange,
}: UseTrimPlayheadParams) => {
  const freePlayheadRef = useRef(false);

  const handleTimelineSeek = (time: number) => {
    if (!isTrimMode) {
      seekTo(time);
      return;
    }
    freePlayheadRef.current = true;
    seekToUnclamped(time);
    play();
  };

  const handleTrimRangeChange = (range: TrimRange) => {
    onTrimRangeChange?.(range);
    freePlayheadRef.current = false;
    if (range.startTime !== trimRange?.startTime) {
      seekToUnclamped(range.startTime);
    } else if (isPlaying && currentTime >= range.endTime) {
      pause();
    }
  };

  const releaseFreePlayhead = () => {
    freePlayheadRef.current = false;
  };

  const resolvePlayStart = (hasEnded: boolean) =>
    resolvePlayStartTime({
      currentTime,
      duration,
      trimRange,
      resumeOutsideTrim: freePlayheadRef.current && !hasEnded,
    });

  return {
    handleTimelineSeek,
    handleTrimRangeChange,
    releaseFreePlayhead,
    resolvePlayStart,
  };
};
